import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: '首頁', path: '/' },
  { label: '關於琮恩', path: '/about' },
  { label: '服務項目', path: '/services' },
  { label: '知識分享', path: '/knowledge' },
  { label: '客戶見證', path: '/testimonials' },
  { label: '工具資源', path: '/tools' },
  { label: '常見問答', path: '/faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Determine if current page has a dark hero (home page only uses transparent nav)
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white shadow-sm'
            : isHomePage
              ? 'bg-transparent'
              : 'bg-white/95 backdrop-blur-md border-b border-gray-100'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/38f658e8-fef2-49e2-8ee9-ab4cf6df771d.png"
              alt="保險建築師 Logo"
              className="h-12 w-auto object-contain"
            />
            <span
              className={`font-bold hidden sm:inline transition-colors duration-300 ${
                scrolled || !isHomePage ? 'text-[#5C5470]' : 'text-white'
              }`}
              style={{ fontSize: '1.375rem' }}
            >
              保險建築師
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-md transition-colors duration-200 whitespace-nowrap font-medium
                  ${(scrolled || !isHomePage) ? 'text-[#333333] hover:text-[#D4A373]' : 'text-white/90 hover:text-white'}
                  ${(link.path === '/' ? location.pathname === '/' : location.pathname === link.path)
                    ? ((scrolled || !isHomePage) ? 'text-[#D4A373] font-semibold' : 'text-white font-semibold')
                    : ''}
                `}
                style={{ fontSize: '1.0625rem' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/contact"
              className="bg-[#D4A373] text-white px-6 py-2.5 rounded-full font-semibold whitespace-nowrap hover:bg-[#c4935f] transition-colors duration-200"
              style={{ fontSize: '1.0625rem' }}
            >
              立即諮詢
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden flex flex-col gap-1.5 p-2 cursor-pointer z-10"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? '關閉選單' : '開啟選單'}
            aria-expanded={menuOpen}
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${(scrolled || !isHomePage || menuOpen) ? 'bg-[#5C5470]' : 'bg-white'} ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${(scrolled || !isHomePage || menuOpen) ? 'bg-[#5C5470]' : 'bg-white'} ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${(scrolled || !isHomePage || menuOpen) ? 'bg-[#5C5470]' : 'bg-white'} ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[80%] max-w-sm bg-white z-50 lg:hidden transition-transform duration-300 ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between px-6 h-20 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <img
              src="https://public.readdy.ai/ai/img_res/38f658e8-fef2-49e2-8ee9-ab4cf6df771d.png"
              alt="保險建築師 Logo"
              className="h-10 w-auto object-contain"
            />
            <span className="font-bold text-[#5C5470]" style={{ fontSize: '1.25rem' }}>
              保險建築師
            </span>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            aria-label="關閉選單"
          >
            <i className="ri-close-line text-[#5C5470] text-xl"></i>
          </button>
        </div>

        {/* Nav Links */}
        <div className="px-6 py-4 flex flex-col">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 py-4 border-b border-gray-100 font-medium transition-colors duration-200
                ${(link.path === '/' ? location.pathname === '/' : location.pathname === link.path) ? 'text-[#D4A373]' : 'text-[#333333] hover:text-[#D4A373]'}
              `}
              style={{ fontSize: '1.125rem' }}
            >
              {(link.path === '/' ? location.pathname === '/' : location.pathname === link.path) && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A373] flex-shrink-0"></span>
              )}
              {link.label}
            </Link>
          ))}

          <Link
            to="/contact"
            className="mt-6 bg-[#D4A373] text-white text-center py-4 rounded-full font-semibold whitespace-nowrap hover:bg-[#c4935f] transition-colors duration-200"
            style={{ fontSize: '1.125rem' }}
          >
            立即諮詢
          </Link>

          {/* LINE quick link */}
          <a
            href="https://lin.ee/6XFYSq2"
            className="mt-3 border-2 border-[#5C5470] text-[#5C5470] text-center py-3.5 rounded-full font-semibold whitespace-nowrap hover:bg-[#5C5470] hover:text-white transition-colors duration-200 flex items-center justify-center gap-2"
            style={{ fontSize: '1.125rem' }}
            aria-label="加入 LINE 諮詢"
          >
            <i className="ri-line-fill"></i>
            加入 LINE 諮詢
          </a>
        </div>
      </div>
    </>
  );
}
